import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isAddress } from 'ethers/lib/utils'
import { useGA } from '~/hooks'
import { useToken } from '~/hooks/useToken'
import { useAppDispatch } from '~/state/hooks'
import { useLimitSwapPair } from '~/state/dex/hooks'
import {
  addCustomLimitTokens,
  updateLimitSwapPairToken,
} from '~/state/dex/actions'
import { useDirectionChange } from '~/components/LimitOrder/hooks/useDirectionChange'
import { ApplicationModal } from '~/state/application/reducer'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import Modal, { Icon } from '~/components/Modal'
import {
  TokenSearchInput,
  EmptySearchResultLite,
} from '../TokenListModal/components'
import TokenListItem from '../TokenListModal/components/TokenListItem'
import TokenSelectWarning from '~/components/Exchange/components/TokenSelectWarning'
import { TradeDirection } from '~/components/Exchange/types'
import { OrderedLimitSwapToken } from '~/components/LimitOrder/hooks/useOrderedTokenList'
import { Container } from './styled'
import Back from '~/assets/images/left.png'
import { useWeb3React } from '@web3-react/core'

export interface AddCustomTokenModalProps {
  direction: TradeDirection
  onDismiss: () => void
}

// ref LimitOrder/components/TokenListModal
const AddCustomTokenModal: React.FC<AddCustomTokenModalProps> = ({
  direction,
  onDismiss,
}) => {
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const { chainId } = useWeb3React()

  const [inputToken, outputToken] = useLimitSwapPair()
  const changeDirection = useDirectionChange()

  const dispatch = useAppDispatch()
  const toggle = useModalToggle(ApplicationModal.LO_ADD_CUSTOM_TOKEN)
  const isOpen = useModalOpened(ApplicationModal.LO_ADD_CUSTOM_TOKEN)

  const [searchText, setSearchText] = useState('')
  const [warningToken, setWarningToken] = useState<LimitSwapToken | undefined>()
  const [tokenWarningVisible, setTokenWarningVisible] = useState(false)

  const customTokenAddress = useMemo(
    () => (isAddress(searchText) ? searchText : ''),
    [searchText],
  )

  const customToken = useToken(customTokenAddress, false) as LimitSwapToken

  const onSearch = (s: string) => {
    setSearchText(s)
  }

  const onSelect = (token: OrderedLimitSwapToken) => {
    trackClick(
      `webtl_lo_${
        direction === TradeDirection.Input ? 'taker' : 'maker'
      }_select`,
      { symbol: token.symbol, address: token.address },
    )
    if (token.blocked) {
      setWarningToken(token)
      setTokenWarningVisible(true)
      return
    }
    if (
      (direction === TradeDirection.Input &&
        token.address === outputToken?.address) ||
      (direction === TradeDirection.Output &&
        token.address === inputToken?.address)
    ) {
      changeDirection()
    } else {
      dispatch(
        updateLimitSwapPairToken({
          direction,
          token: {
            chainId: token.chainId,
            symbol: token.symbol,
            address: token.address,
            name: token.name,
            decimals: token.decimals,
            logo: token.logo,
            displayType: token.displayType,
            recommend: token.recommend,
          },
        }),
      )
    }
    toggle()
    onDismiss()
  }

  const onSelectCustomToken = () => {
    setWarningToken(customToken)
    setTokenWarningVisible(true)
  }

  const onWarningClose = () => {
    setTokenWarningVisible(false)
  }

  const onWarningSelect = () => {
    if (
      warningToken.address.toLowerCase() === customToken.address.toLowerCase()
    ) {
      dispatch(
        addCustomLimitTokens({
          chainId,
          tokens: [
            {
              ...customToken,
              displayType: 999,
              recommend: false,
            },
          ],
        }),
      )
      dispatch(updateLimitSwapPairToken({ direction, token: customToken }))
    }
    onWarningClose()
    onSelect({
      chainId,
      symbol: customToken.symbol,
      address: customToken.address,
      name: customToken.name,
      decimals: customToken.decimals,
      logo: customToken.logo,
      displayType: 999,
      recommend: false,
    })
  }

  const isEmptyResult = !!searchText && !customToken

  return (
    <Modal
      title={t('import_token')}
      left={<Icon url={Back.src} onClick={toggle} />}
      isOpen={isOpen}
      onDismiss={toggle}
      animate={false}
      headerClassName="addCustomToken"
    >
      <Container>
        <TokenSearchInput
          onSearch={onSearch}
          placeholder="search_by_token_address"
        />
        {isEmptyResult && <EmptySearchResultLite isSearchByAddress={true} />}
        {customToken && (
          <TokenListItem
            showContractAddr
            showNetworkBadge={true}
            custom
            token={customToken}
            onSelect={onSelectCustomToken}
          />
        )}
        <TokenSelectWarning
          visible={tokenWarningVisible}
          token={warningToken}
          blocked={warningToken && warningToken.blocked}
          onClose={onWarningClose}
          onSelect={onWarningSelect}
        />
      </Container>
    </Modal>
  )
}

export default AddCustomTokenModal
